import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Plus,
  HelpCircle,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  Calendar,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  FileText,
  Save,
  Loader2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import FormStepper from '../common/FormStepper';
import ContractService from '../../services/contractService';
import RenewalAlertsService from '../../services/renewalAlertsService';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const formatDateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const getDefaultFormData = () => ({
  contractId: '',
  expiryDate: '',
  renewalStartDate: '',
  renewalEndDate: '',
  autoRenew: false,
  autoRenewIntervalMonths: 12,
  alertThresholds: '90,60,30',
  alertRecipients: '',
  notes: '',
});

const ContractRenewalsAlerts = () => {
  const { success, error: showError, toasts, removeToast } = useToast();
  const { openPageHelp } = useHelp();

  const [renewalAlerts, setRenewalAlerts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  // viewMode: 'list' | 'create' | 'edit' | 'view'
  const [viewMode, setViewMode] = useState('list');
  const [formMode, setFormMode] = useState('create');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stepper
  const [currentStep, setCurrentStep] = useState(0);

  const STEPS = [
    {
      id: 0,
      label: 'Contract & Dates',
      icon: FileText,
      hint: 'Select contract and set expiry / renewal dates',
    },
    {
      id: 1,
      label: 'Auto-Renewal',
      icon: RefreshCw,
      hint: 'Configure automatic renewal settings',
    },
    {
      id: 2,
      label: 'Alert Settings',
      icon: AlertTriangle,
      hint: 'Set thresholds and notification recipients',
    },
    {
      id: 3,
      label: 'Notes & Review',
      icon: Save,
      hint: 'Add notes and confirm details',
    },
  ];

  // ─── Data loading ───────────────────────────────────────────────────────────
  const loadContracts = async () => {
    try {
      const response = await ContractService.getUserContracts({
        includeInactive: true,
      });
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setContracts(list);
    } catch (error) {
      console.error('Failed to load contracts', error);
      showError('Unable to load contracts for renewals and alerts');
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await RenewalAlertsService.list('all');
      setRenewalAlerts(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch renewal alerts', error);
      showError('Failed to load renewal alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
    loadAlerts();
  }, []);

  // ─── Derived data ───────────────────────────────────────────────────────────
  const filteredAlerts = useMemo(() => {
    if (activeTab === 'pending')
      return renewalAlerts.filter((a) => a.alertStatus === 'pending');
    if (activeTab === 'sent')
      return renewalAlerts.filter((a) => a.alertStatus === 'sent');
    if (activeTab === 'past-due')
      return renewalAlerts.filter((a) => a.isPastDue);
    return renewalAlerts;
  }, [activeTab, renewalAlerts]);

  const summaryStats = useMemo(() => {
    const pending = renewalAlerts.filter(
      (a) => a.alertStatus === 'pending',
    ).length;
    const sent = renewalAlerts.filter((a) => a.alertStatus === 'sent').length;
    const pastDue = renewalAlerts.filter((a) => a.isPastDue).length;
    const autoRenew = renewalAlerts.filter((a) => a.autoRenew).length;
    const nextAlert = renewalAlerts
      .filter((a) => a.nextAlertAt)
      .map((a) => new Date(a.nextAlertAt))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b)[0];
    return {
      total: renewalAlerts.length,
      pending,
      sent,
      pastDue,
      autoRenew,
      nextAlert: nextAlert ? nextAlert.toLocaleDateString() : 'None',
    };
  }, [renewalAlerts]);

  const tableData = useMemo(
    () =>
      filteredAlerts.map((alert) => ({
        ...alert,
        id: alert._id,
        contractTitle: alert.contract?.title || alert.contractId || '—',
        counterparty: alert.contract?.counterparty || '—',
        alertThresholds: Array.isArray(alert.alertThresholds)
          ? alert.alertThresholds.join(', ')
          : alert.alertThresholds || '—',
      })),
    [filteredAlerts],
  );

  // ─── KPI Cards ──────────────────────────────────────────────────────────────
  const kpiCards = [
    {
      value: summaryStats.total,
      label: 'Total Configurations',
      icon: Shield,
      iconColor: '#2D7DD2',
      iconBg: '#D6EFFF',
    },
    {
      value: summaryStats.pending,
      label: 'Pending Alerts',
      icon: Clock,
      iconColor: '#F59E0B',
      iconBg: '#FEF3C7',
    },
    {
      value: summaryStats.sent,
      label: 'Sent Alerts',
      icon: CheckCircle,
      iconColor: '#059669',
      iconBg: '#D1FAE5',
    },
    {
      value: summaryStats.pastDue,
      label: 'Past Due',
      icon: AlertTriangle,
      iconColor: '#EF4444',
      iconBg: '#FEE2E2',
    },
    {
      value: summaryStats.autoRenew,
      label: 'Auto-Renew Enabled',
      icon: RefreshCw,
      iconColor: '#7C3AED',
      iconBg: '#EDE9FE',
    },
    {
      value: summaryStats.nextAlert,
      label: 'Next Alert',
      icon: Calendar,
      iconColor: '#06B6D4',
      iconBg: '#DFF9FF',
    },
  ];

  // ─── Tabs ───────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'all', label: 'All Configurations', icon: Shield },
    { id: 'pending', label: 'Pending Alerts', icon: Clock },
    { id: 'sent', label: 'Sent Alerts', icon: CheckCircle },
    { id: 'past-due', label: 'Past Due', icon: AlertTriangle },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // ─── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      key: 'contractTitle',
      label: 'Contract',
      width: '240px',
      render: (_, alert) => (
        <div>
          <div className="fw-medium">
            {alert.contract?.title || alert.contractId}
          </div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {alert.contractId}
          </div>
        </div>
      ),
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: '160px',
      render: (_, alert) => alert.contract?.counterparty || '—',
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      width: '130px',
      render: (value) => formatDateLabel(value),
    },
    {
      key: 'daysUntilExpiry',
      label: 'Days Left',
      width: '110px',
      render: (value) => (
        <span
          style={{
            color: value <= 0 ? '#dc2626' : '#059669',
            fontWeight: '500',
          }}
        >
          {value <= 0 ? 'Expired' : `${value} days`}
        </span>
      ),
    },
    {
      key: 'nextAlertAt',
      label: 'Next Alert',
      width: '130px',
      render: (value) => formatDateLabel(value),
    },
    {
      key: 'alertStatus',
      label: 'Alert Status',
      width: '130px',
      render: (value) => (
        <span
          className={`badge ${value === 'sent' ? 'badge-success' : 'badge-warning'} d-flex align-items-center gap-1`}
        >
          {value === 'sent' ? <CheckCircle size={14} /> : <Clock size={14} />}
          {value?.toUpperCase() || 'PENDING'}
        </span>
      ),
    },
    {
      key: 'alertThresholds',
      label: 'Thresholds (days)',
      width: '140px',
      render: (value) =>
        Array.isArray(value) ? value.join(', ') : value || '—',
    },
    {
      key: 'autoRenew',
      label: 'Auto-Renew',
      width: '110px',
      render: (value) => (
        <span
          className={`badge ${value ? 'badge-success' : 'badge-secondary'}`}
        >
          {value ? 'Enabled' : 'Manual'}
        </span>
      ),
    },
  ];

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    loadAlerts().then(() => success('Renewal alerts refreshed'));
  };

  const handleAddNew = () => {
    setSelectedAlert(null);
    setFormMode('create');
    setFormData(getDefaultFormData());
    setCurrentStep(0);
    setViewMode('create');
  };

  const handleEdit = (alert) => {
    setSelectedAlert(alert);
    setFormMode('edit');
    setFormData({
      contractId: alert.contractId,
      expiryDate: formatDateForInput(alert.expiryDate),
      renewalStartDate: formatDateForInput(alert.renewalDates?.[0]?.startDate),
      renewalEndDate: formatDateForInput(alert.renewalDates?.[0]?.endDate),
      autoRenew: Boolean(alert.autoRenew),
      autoRenewIntervalMonths: alert.autoRenewIntervalMonths || 12,
      alertThresholds: (alert.alertThresholds || []).join(','),
      alertRecipients: (alert.alertRecipients || []).join(', '),
      notes: alert.notes || '',
    });
    setCurrentStep(0);
    setViewMode('edit');
  };

  const handleView = (alert) => {
    setSelectedAlert(alert);
    setFormMode('view');
    setFormData({
      contractId: alert.contractId,
      expiryDate: formatDateForInput(alert.expiryDate),
      renewalStartDate: formatDateForInput(alert.renewalDates?.[0]?.startDate),
      renewalEndDate: formatDateForInput(alert.renewalDates?.[0]?.endDate),
      autoRenew: Boolean(alert.autoRenew),
      autoRenewIntervalMonths: alert.autoRenewIntervalMonths || 12,
      alertThresholds: (alert.alertThresholds || []).join(','),
      alertRecipients: (alert.alertRecipients || []).join(', '),
      notes: alert.notes || '',
    });
    setCurrentStep(0);
    setViewMode('view');
  };

  const handleDelete = (alert) => {
    setAlertToDelete(alert);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!alertToDelete) return;
    setIsDeleting(true);
    try {
      await RenewalAlertsService.remove(
        alertToDelete.contractId,
        alertToDelete._id,
      );
      success('Renewal alert removed');
      await loadAlerts();
    } catch (error) {
      console.error('Error deleting renewal alert', error);
      showError(error?.message || 'Failed to delete renewal alert');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setAlertToDelete(null);
    }
  };

  const handleCancelForm = () => {
    setViewMode('list');
    setSelectedAlert(null);
    setCurrentStep(0);
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep) setCurrentStep(stepIndex);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (formMode === 'view') return;

    if (!formData.contractId)
      return showError('Select a contract before saving');
    if (!formData.expiryDate) return showError('Expiry date is required');

    const thresholds = formData.alertThresholds
      .split(/[,;]+/)
      .map((item) => Number(item.trim()))
      .filter((v) => !Number.isNaN(v) && v > 0);

    const recipients = formData.alertRecipients
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const renewalDates = [];
    if (formData.renewalStartDate || formData.renewalEndDate) {
      renewalDates.push({
        startDate: formData.renewalStartDate || formData.expiryDate,
        endDate: formData.renewalEndDate || formData.expiryDate,
      });
    }

    const payload = {
      expiryDate: formData.expiryDate,
      autoRenew: formData.autoRenew,
      autoRenewIntervalMonths: Number(formData.autoRenewIntervalMonths) || 12,
      alertThresholds: thresholds.length ? thresholds : undefined,
      alertRecipients: recipients.length ? recipients : undefined,
      renewalDates: renewalDates.length ? renewalDates : undefined,
      notes: formData.notes,
    };

    setIsSubmitting(true);
    try {
      if (formMode === 'create') {
        const result = await RenewalAlertsService.create(
          formData.contractId,
          payload,
        );
        if (result?.success === false) {
          showError(result?.message || 'Failed to create renewal alert');
          return;
        }
        success('Renewal alert configuration saved');
      } else if (selectedAlert) {
        const result = await RenewalAlertsService.update(
          formData.contractId,
          selectedAlert._id,
          payload,
        );
        if (result?.success === false) {
          showError(result?.message || 'Failed to update renewal alert');
          return;
        }
        success('Renewal alert configuration updated');
      }
      setViewMode('list');
      setSelectedAlert(null);
      await loadAlerts();
    } catch (error) {
      console.error('Error saving renewal alert', error);
      showError(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to save renewal alert',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Step content ───────────────────────────────────────────────────────────
  const renderStepContent = (disabled = false) => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contract *
              </label>
              <select
                value={formData.contractId}
                onChange={(e) => handleFormChange('contractId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={disabled || formMode === 'edit'}
              >
                <option value="">Select a contract</option>
                {contracts.map((contract) => (
                  <option key={contract.contractId} value={contract.contractId}>
                    {contract.title} — {contract.counterparty}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date *
                </label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    handleFormChange('expiryDate', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={disabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Renewal Start Date
                </label>
                <input
                  type="date"
                  value={formData.renewalStartDate}
                  onChange={(e) =>
                    handleFormChange('renewalStartDate', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={disabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Renewal End Date
                </label>
                <input
                  type="date"
                  value={formData.renewalEndDate}
                  onChange={(e) =>
                    handleFormChange('renewalEndDate', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="autoRenew"
                checked={formData.autoRenew}
                onChange={(e) =>
                  handleFormChange('autoRenew', e.target.checked)
                }
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                disabled={disabled}
              />
              <label
                htmlFor="autoRenew"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                Enable automatic renewal
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-Renew Interval (months)
              </label>
              <input
                type="number"
                min="1"
                value={formData.autoRenewIntervalMonths}
                onChange={(e) =>
                  handleFormChange('autoRenewIntervalMonths', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={disabled || !formData.autoRenew}
              />
              {!formData.autoRenew && (
                <p className="mt-1 text-xs text-gray-500">
                  Enable automatic renewal above to configure this field.
                </p>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alert Thresholds (days before expiry)
              </label>
              <input
                type="text"
                value={formData.alertThresholds}
                onChange={(e) =>
                  handleFormChange('alertThresholds', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 90,60,30"
                disabled={disabled}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter comma-separated days. Alerts will be sent at each
                threshold.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alert Recipients
              </label>
              <input
                type="text"
                value={formData.alertRecipients}
                onChange={(e) =>
                  handleFormChange('alertRecipients', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email1@example.com, email2@example.com"
                disabled={disabled}
              />
              <p className="mt-1 text-xs text-gray-500">
                Comma-separated list of email addresses to notify.
              </p>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optional notes about this renewal configuration"
                disabled={disabled}
              />
            </div>
            {/* Review summary */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Review Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-700">Contract</p>
                  <p className="text-sm text-gray-900">
                    {contracts.find((c) => c.contractId === formData.contractId)
                      ?.title ||
                      formData.contractId ||
                      'Not selected'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Expiry Date
                  </p>
                  <p className="text-sm text-gray-900">
                    {formData.expiryDate || 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Renewal Start
                  </p>
                  <p className="text-sm text-gray-900">
                    {formData.renewalStartDate || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Renewal End
                  </p>
                  <p className="text-sm text-gray-900">
                    {formData.renewalEndDate || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Auto-Renew
                  </p>
                  <p className="text-sm text-gray-900">
                    {formData.autoRenew
                      ? `Enabled (every ${formData.autoRenewIntervalMonths} months)`
                      : 'Disabled'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Alert Thresholds
                  </p>
                  <p className="text-sm text-gray-900">
                    {formData.alertThresholds || '—'} days
                  </p>
                </div>
              </div>
              {formData.alertRecipients && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">
                    Alert Recipients
                  </p>
                  <p className="text-sm text-gray-900">
                    {formData.alertRecipients}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ─── Analytics tab ──────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const COLORS = [
      '#4678be',
      '#059669',
      '#F59E0B',
      '#EF4444',
      '#7C3AED',
      '#06B6D4',
    ];

    const statusData = [
      { name: 'Pending', value: summaryStats.pending },
      { name: 'Sent', value: summaryStats.sent },
      { name: 'Past Due', value: summaryStats.pastDue },
    ];

    const autoRenewData = [
      { name: 'Auto-Renew', value: summaryStats.autoRenew },
      { name: 'Manual', value: summaryStats.total - summaryStats.autoRenew },
    ];

    return (
      <div style={{ marginTop: '32px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Alert Status Distribution
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {statusData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Auto-Renew vs Manual
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={autoRenewData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#4678be"
                    name="Contracts"
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            padding: '24px',
          }}
        >
          <h5
            style={{
              marginBottom: '12px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Renewal Health Summary
          </h5>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            <div
              style={{
                padding: '16px',
                border: '1px solid var(--border-color)',
              }}
            >
              <strong style={{ fontSize: '14px' }}>Alert Coverage</strong>
              <p
                style={{
                  marginTop: '8px',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                }}
              >
                Keep thresholds at 30/60/90 days to ensure coverage at every
                renewal stage.
              </p>
            </div>
            <div
              style={{
                padding: '16px',
                border: '1px solid var(--border-color)',
              }}
            >
              <strong style={{ fontSize: '14px' }}>Auto-Renew Impact</strong>
              <p
                style={{
                  marginTop: '8px',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                }}
              >
                {summaryStats.autoRenew > 0
                  ? 'Auto-renewing contracts are shielded from lapses.'
                  : 'No contracts are set to auto-renew. Consider enabling it to prevent lapses.'}
              </p>
            </div>
            <div
              style={{
                padding: '16px',
                border: '1px solid var(--border-color)',
              }}
            >
              <strong style={{ fontSize: '14px' }}>Past Due</strong>
              <p
                style={{
                  marginTop: '8px',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                }}
              >
                {summaryStats.pastDue > 0
                  ? `${summaryStats.pastDue} contract(s) are past due and need immediate attention.`
                  : 'No contracts are past due. Great job!'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Full-page form view ────────────────────────────────────────────────────
  if (viewMode === 'create' || viewMode === 'edit' || viewMode === 'view') {
    const isViewOnly = viewMode === 'view';
    const saveLabel =
      viewMode === 'edit'
        ? 'Save Changes'
        : viewMode === 'view'
          ? 'Close'
          : 'Save Configuration';
    const formTitle =
      viewMode === 'edit'
        ? 'Edit Renewal & Alert'
        : viewMode === 'view'
          ? 'Renewal & Alert Details'
          : 'New Renewal & Alert';
    const formDescription =
      viewMode === 'edit'
        ? 'Update renewal dates, thresholds, and notification routing'
        : viewMode === 'view'
          ? 'View renewal and alert configuration details'
          : 'Configure renewal sequencing, alert thresholds, and notification routing';

    const formHeaderActions = isViewOnly
      ? [{ label: 'Close', onClick: handleCancelForm, variant: 'outline' }]
      : [
          { label: 'Cancel', onClick: handleCancelForm, variant: 'outline' },
          {
            label: saveLabel,
            icon: isSubmitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            ),
            onClick: () =>
              document.getElementById('renewal-form')?.requestSubmit(),
            variant: 'primary',
            disabled: isSubmitting,
          },
        ];

    return (
      <>
        {/* Breadcrumb */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '0 32px',
            marginBottom: '4px',
            marginTop: '16px',
            fontSize: '13px',
            color: 'var(--text-secondary)',
          }}
        >
          <button
            type="button"
            onClick={handleCancelForm}
            style={{
              background: 'none',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span>Dashboard</span>
          <ChevronRight size={14} />
          <span>Contracts</span>
          <ChevronRight size={14} />
          <span>Contract Renewals & Alerts</span>
          <ChevronRight size={14} />
          <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
            {viewMode === 'create'
              ? 'Create'
              : viewMode === 'edit'
                ? 'Edit'
                : 'View'}
          </span>
        </div>

        <PageHeader
          title={formTitle}
          description={formDescription}
          actions={formHeaderActions}
        />

        <div className="m-8 mt-0">
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              padding: '32px',
            }}
          >
            <FormStepper
              steps={STEPS}
              currentStep={currentStep}
              onStepClick={handleStepClick}
            />

            <div style={{ marginTop: '32px' }}>
              <form id="renewal-form" onSubmit={handleFormSubmit}>
                {renderStepContent(isViewOnly)}

                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                    Back
                  </button>

                  <div className="flex gap-3">
                    {currentStep < STEPS.length - 1 ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    ) : (
                      !isViewOnly && (
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              {viewMode === 'edit'
                                ? 'Saving...'
                                : 'Creating...'}
                            </>
                          ) : (
                            <>
                              <Save size={16} />
                              {saveLabel}
                            </>
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </>
    );
  }

  // ─── List view ──────────────────────────────────────────────────────────────
  const listHeaderActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    {
      label: 'New Renewal & Alert',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp('contract-renewals'),
      variant: 'outline',
      tooltip: 'Open renewal help',
    },
  ];

  return (
    <>
      <PageBreadcrumb
        items={[
          { label: 'Contracts', path: '/contracts' },
          { label: 'Contract Renewals & Alerts' },
        ]}
      />
      <PageHeader
        title="Contract Renewals & Alerts"
        description="Manage renewal dates, configure alert thresholds, and keep expiration workflows aligned."
        actions={listHeaderActions}
      />

      {/* KPI Cards */}
      <div className="m-8">
        <div className="grid-modern-4 w-full gap-6">
          {kpiCards.map((card, index) => {
            const IconComponent = card.icon;
            return (
              <div
                key={index}
                className="relative border border-[var(--border-color)] bg-white"
              >
                <div className="h-1 w-full bg-[var(--primary-color)]" />
                <div className="flex items-center gap-3 p-4">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center"
                    style={{ background: card.iconBg }}
                  >
                    <IconComponent
                      size={20}
                      style={{ color: card.iconColor }}
                    />
                  </div>
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {card.value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {activeTab === 'analytics' ? (
          <div
            className="analytics-panel"
            style={{
              marginTop: '24px',
              background: '#fff',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div>
                <h4>Alert Delivery</h4>
                <p>
                  {summaryStats.sent} alerts have been sent,{' '}
                  {summaryStats.pending} remain pending.
                </p>
              </div>
              <div>
                <h4>Renewal Health</h4>
                <p>
                  {summaryStats.pastDue} contracts are past due;{' '}
                  {summaryStats.autoRenew} are configured for automatic renewal.
                </p>
              </div>
            </div>
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  flex: '1 1 220px',
                  padding: '16px',
                  borderRadius: '10px',
                  background: 'var(--primary-color)',
                  color: '#fff',
                }}
              >
                <strong>Ensure Coverage</strong>
                <p style={{ marginTop: '8px' }}>
                  Keep alerts ahead of every stage by keeping thresholds at
                  30/60/90 days.
                </p>
              </div>
              <div
                style={{
                  flex: '1 1 220px',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                }}
              >
                <strong>Auto-Renew Impact</strong>
                <p style={{ marginTop: '8px' }}>
                  Auto-renewing contracts are{' '}
                  {summaryStats.autoRenew > 0
                    ? 'shielded from lapses'
                    : 'waiting for manual updates'}
                  .
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              marginTop: '24px',
              background: '#fff',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <DataTable
              title="Renewal & Alert Configurations"
              data={tableData}
              columns={columns}
              searchable
              sortable
              exportable
              pagination
              pageSize={10}
              showActions
              showCheckboxes={false}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading}
              searchPlaceholder="Search contracts, statuses, or alert thresholds"
              emptyMessage="No renewal alerts configured yet. Click 'New Renewal & Alert' to get started."
            />
          </div>
        )}
      </div>

      <div style={{ width: '100%', maxWidth: '100%' }}>
        <div className="m-8 mt-0">
          {/* Tabs */}
          <div className="mb-8 border border-slate-200">
            <div className="flex items-center w-full bg-white border-b border-slate-200">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center gap-2 px-6 py-3 w-full text-sm font-medium border-b-2 transition-colors duration-200 ${
                      isActive
                        ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                        : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <IconComponent size={18} />
                    <span className="whitespace-nowrap">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          {activeTab !== 'analytics' && (
            <div className="page-tab-content" style={{ marginTop: '32px' }}>
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    flexDirection: 'column',
                    gap: '16px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading renewal alerts...
                  </p>
                </div>
              ) : (
                <div
                  className="page-table-container"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <DataTable
                    data={tableData}
                    columns={columns}
                    title="Renewal & Alert Configurations"
                    searchable={true}
                    sortable={true}
                    exportable={true}
                    pagination={true}
                    pageSize={10}
                    showActions={true}
                    showCheckboxes={false}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchPlaceholder="Search contracts, statuses, or alert thresholds..."
                    emptyMessage="No renewal alerts configured yet. Click 'New Renewal & Alert' to get started."
                    initialHiddenColumns={['id']}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && renderAnalytics()}
        </div>

        {/* Delete Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Renewal Alert"
          message={`Remove the renewal alert for "${alertToDelete?.contract?.title || alertToDelete?.contractId}"? This will stop all scheduled notifications.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <PageHelp />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ContractRenewalsAlerts;